import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  DollarSign,
  Eye,
  Trash2,
  Edit,
  Copy,
  UserCheck,
  AlertCircle,
  ShoppingCart,
  Check,
  Printer,
  Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import DocumentViewer from '../components/DocumentViewer';
import axios from 'axios';
import { toast } from 'sonner';
import { useSettings } from '../context/SettingsContext';
import SARSymbol from '../components/SARSymbol';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency display helper
const CurrencyValue = ({ value, symbol, isSAR, size = 14 }) => {
  if (isSAR) {
    return (
      <span className="inline-flex items-center gap-1">
        <SARSymbol size={size} />
        <span>{value}</span>
      </span>
    );
  }
  return <span>{symbol} {value}</span>;
};

const Quotations = () => {
  const { currencySymbol, formatCurrency, isSAR } = useSettings();
  const [quotations, setQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [stats, setStats] = useState({ total: 0, draft: 0, sent: 0, accepted: 0, pending_approval: 0, total_value: 0 });
  const [template, setTemplate] = useState(null);
  const { toast: showToast } = useToast();
  
  const [formData, setFormData] = useState({
    client_id: '',
    currency: 'USD',
    items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
    valid_until: '',
    notes: '',
    terms: 'Payment due within 30 days of acceptance.'
  });

  useEffect(() => {
    fetchQuotations();
    fetchClients();
    fetchProducts();
    fetchStats();
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/templates/default?document_type=quotation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(res.data);
    } catch (err) {
      console.log('No template found, using defaults');
    }
  };

  const fetchQuotations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/sales/quotations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotations(res.data);
    } catch (err) {
      toast.error('Failed to fetch quotations');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/crm/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(res.data);
    } catch (err) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/procurement/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data);
    } catch (err) {
      console.error('Failed to fetch products');
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/sales/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data.quotations);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    return { subtotal, tax_amount: subtotal * 0.1, total: subtotal * 1.1 };
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems.length ? newItems : [{ description: '', quantity: 1, unit_price: 0, total: 0 }] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const totals = calculateTotals(formData.items);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/sales/quotations`, {
        ...formData,
        ...totals,
        status: 'draft'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quotation created successfully');
      setDialogOpen(false);
      fetchQuotations();
      fetchStats();
      resetForm();
    } catch (err) {
      toast.error('Failed to create quotation');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      currency: 'USD',
      items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
      valid_until: '',
      notes: '',
      terms: 'Payment due within 30 days of acceptance.'
    });
  };

  const handleViewQuotation = (quote) => {
    setSelectedQuotation(quote);
    setViewDialogOpen(true);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  const sendQuotation = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/sales/quotations/${id}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quotation sent to customer');
      fetchQuotations();
      fetchStats();
    } catch (err) {
      toast.error('Failed to send quotation');
    }
  };

  const submitForApproval = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/sales/quotations/${id}/submit-for-approval`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quotation submitted for manager approval');
      fetchQuotations();
      fetchStats();
    } catch (err) {
      toast.error('Failed to submit for approval');
    }
  };

  const approveQuotation = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/sales/quotations/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast({
        title: "Quotation Approved!",
        description: `Sales Order ${res.data.sales_order.order_number} has been created.`,
      });
      setApprovalDialogOpen(false);
      fetchQuotations();
      fetchStats();
    } catch (err) {
      toast.error('Failed to approve quotation');
    }
  };

  const rejectQuotation = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/sales/quotations/${id}/reject?reason=${encodeURIComponent(rejectionReason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quotation rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchQuotations();
      fetchStats();
    } catch (err) {
      toast.error('Failed to reject quotation');
    }
  };

  const convertToOrder = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/sales/quotations/${id}/convert`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quotation converted to Sales Order');
      fetchQuotations();
      fetchStats();
    } catch (err) {
      toast.error('Failed to convert quotation');
    }
  };

  const deleteQuotation = async (id) => {
    if (!window.confirm('Delete this quotation?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/sales/quotations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quotation deleted');
      fetchQuotations();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete quotation');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700',
      pending_approval: 'bg-amber-100 text-amber-700',
      sent: 'bg-blue-100 text-blue-700',
      viewed: 'bg-purple-100 text-purple-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      expired: 'bg-orange-100 text-orange-700',
      converted: 'bg-indigo-100 text-indigo-700'
    };
    const labels = {
      draft: 'Draft',
      pending_approval: 'Pending Approval',
      sent: 'Sent',
      viewed: 'Viewed',
      accepted: 'Accepted',
      approved: 'Approved',
      rejected: 'Rejected',
      expired: 'Expired',
      converted: 'Converted'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{labels[status] || status}</Badge>;
  };

  const filteredQuotations = quotations.filter(q => {
    const matchesSearch = q.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          q.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Quotations</h1>
          <p className="text-muted-foreground">Create and manage sales quotations</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> New Quotation
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-xs text-muted-foreground">Total Quotes</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-slate-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.draft}</div>
                <div className="text-xs text-muted-foreground">Drafts</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={stats.pending_approval > 0 ? 'ring-2 ring-amber-500' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.pending_approval || 0}</div>
                <div className="text-xs text-muted-foreground">Pending Approval</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.accepted}</div>
                <div className="text-xs text-muted-foreground">Accepted</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <div className="text-2xl font-bold"><CurrencyValue value={stats.total_value?.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div>
                <div className="text-xs text-muted-foreground">Total Value</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search quotations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Quotations List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium">Quote #</th>
                  <th className="text-left p-4 font-medium">Client</th>
                  <th className="text-left p-4 font-medium">Amount</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-left p-4 font-medium">Valid Until</th>
                  <th className="text-left p-4 font-medium">Created</th>
                  <th className="text-right p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotations.map((quote) => (
                  <tr key={quote.id} className="border-b hover:bg-muted/50">
                    <td className="p-4 font-medium">{quote.quote_number}</td>
                    <td className="p-4">{quote.client_name}</td>
                    <td className="p-4 font-medium">{currencySymbol} {quote.total?.toLocaleString()}</td>
                    <td className="p-4">{getStatusBadge(quote.status)}</td>
                    <td className="p-4 text-muted-foreground">
                      {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {new Date(quote.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedQuotation(quote);
                            setDocumentViewerOpen(true);
                          }}>
                            <Eye className="h-4 w-4 mr-2" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setSelectedQuotation(quote);
                            setDocumentViewerOpen(true);
                            // Print will be triggered from DocumentViewer
                          }}>
                            <Printer className="h-4 w-4 mr-2" /> Print
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setSelectedQuotation(quote);
                            setDocumentViewerOpen(true);
                            // Email dialog will be opened from DocumentViewer
                          }}>
                            <Mail className="h-4 w-4 mr-2" /> Email
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {quote.status === 'draft' && (
                            <>
                              <DropdownMenuItem onClick={() => sendQuotation(quote.id)}>
                                <Send className="h-4 w-4 mr-2" /> Send to Customer
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => submitForApproval(quote.id)}>
                                <UserCheck className="h-4 w-4 mr-2" /> Submit for Approval
                              </DropdownMenuItem>
                            </>
                          )}
                          {quote.status === 'sent' && (
                            <DropdownMenuItem onClick={() => submitForApproval(quote.id)}>
                              <UserCheck className="h-4 w-4 mr-2" /> Submit for Approval
                            </DropdownMenuItem>
                          )}
                          {quote.status === 'accepted' && (
                            <DropdownMenuItem onClick={() => submitForApproval(quote.id)}>
                              <UserCheck className="h-4 w-4 mr-2" /> Submit for Approval
                            </DropdownMenuItem>
                          )}
                          {quote.status === 'pending_approval' && (
                            <>
                              <DropdownMenuItem onClick={() => {
                                setSelectedQuotation(quote);
                                setApprovalDialogOpen(true);
                              }} className="text-green-600">
                                <Check className="h-4 w-4 mr-2" /> Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSelectedQuotation(quote);
                                setRejectDialogOpen(true);
                              }} className="text-red-600">
                                <XCircle className="h-4 w-4 mr-2" /> Reject
                              </DropdownMenuItem>
                            </>
                          )}
                          {quote.status === 'approved' && quote.sales_order_number && (
                            <DropdownMenuItem className="text-blue-600">
                              <ShoppingCart className="h-4 w-4 mr-2" /> View Order: {quote.sales_order_number}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => deleteQuotation(quote.id)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {filteredQuotations.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      No quotations found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Quotation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valid Until</Label>
                <Input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Line Items</Label>
              <div className="border rounded-lg p-4 space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="col-span-2 text-right font-medium">
                      {currencySymbol} {(item.quantity * item.unit_price).toFixed(2)}
                    </div>
                    <div className="col-span-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
            </div>

            <div className="flex justify-end border-t pt-4">
              <div className="text-right space-y-1">
                <div>Subtotal: {currencySymbol} {calculateTotals(formData.items).subtotal.toFixed(2)}</div>
                <div>Tax (10%): {currencySymbol} {calculateTotals(formData.items).tax_amount.toFixed(2)}</div>
                <div className="text-lg font-bold">Total: {currencySymbol} {calculateTotals(formData.items).total.toFixed(2)}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!formData.client_id}>Create Quotation</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Quotation Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Quotation Details - {selectedQuotation?.quotation_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuotation && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Quotation #</p>
                  <p className="font-semibold">{selectedQuotation.quotation_number}</p>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Client</p>
                  <p className="font-semibold">{getClientName(selectedQuotation.client_id)}</p>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge variant={
                    selectedQuotation.status === 'accepted' ? 'default' :
                    selectedQuotation.status === 'sent' ? 'secondary' :
                    selectedQuotation.status === 'rejected' ? 'destructive' : 'outline'
                  }>
                    {selectedQuotation.status}
                  </Badge>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Valid Until</p>
                  <p className="font-semibold">
                    {selectedQuotation.valid_until ? new Date(selectedQuotation.valid_until).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Items Table */}
              <div>
                <h4 className="font-semibold mb-3">Items</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3">#</th>
                        <th className="text-left p-3">Description</th>
                        <th className="text-center p-3">Qty</th>
                        <th className="text-right p-3">Unit Price</th>
                        <th className="text-right p-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuotation.items?.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-3">{idx + 1}</td>
                          <td className="p-3">{item.description}</td>
                          <td className="p-3 text-center">{item.quantity}</td>
                          <td className="p-3 text-right">{selectedQuotation.currency} {item.unit_price?.toFixed(2)}</td>
                          <td className="p-3 text-right font-medium">{selectedQuotation.currency} {item.total?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/50">
                      <tr className="border-t">
                        <td colSpan={4} className="p-3 text-right font-semibold">Subtotal:</td>
                        <td className="p-3 text-right font-semibold">
                          {selectedQuotation.currency} {selectedQuotation.subtotal?.toFixed(2)}
                        </td>
                      </tr>
                      {selectedQuotation.tax > 0 && (
                        <tr>
                          <td colSpan={4} className="p-3 text-right">Tax ({selectedQuotation.tax_rate || 15}%):</td>
                          <td className="p-3 text-right">
                            {selectedQuotation.currency} {selectedQuotation.tax?.toFixed(2)}
                          </td>
                        </tr>
                      )}
                      <tr className="border-t-2">
                        <td colSpan={4} className="p-3 text-right font-bold text-lg">Total:</td>
                        <td className="p-3 text-right font-bold text-lg text-primary">
                          {selectedQuotation.currency} {selectedQuotation.total?.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Notes & Terms */}
              <div className="grid md:grid-cols-2 gap-4">
                {selectedQuotation.notes && (
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Notes</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedQuotation.notes}</p>
                  </div>
                )}
                {selectedQuotation.terms && (
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Terms & Conditions</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedQuotation.terms}</p>
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="flex justify-between text-xs text-muted-foreground border-t pt-4">
                <span>Created: {new Date(selectedQuotation.created_at).toLocaleString()}</span>
                {selectedQuotation.sent_at && (
                  <span>Sent: {new Date(selectedQuotation.sent_at).toLocaleString()}</span>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            {selectedQuotation?.status === 'draft' && (
              <Button onClick={() => {
                sendQuotation(selectedQuotation.id);
                setViewDialogOpen(false);
              }}>
                <Send className="h-4 w-4 mr-2" /> Send Quotation
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Approve Quotation
            </DialogTitle>
            <DialogDescription>
              This will approve the quotation and automatically create a Sales Order.
            </DialogDescription>
          </DialogHeader>
          
          {selectedQuotation && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Quotation:</span>
                  <span className="font-semibold">{selectedQuotation.quote_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client:</span>
                  <span className="font-semibold">{selectedQuotation.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-semibold text-primary">
                    {selectedQuotation.currency} {selectedQuotation.total?.toLocaleString()}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-700">
                  A new Sales Order will be created automatically upon approval.
                </span>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => approveQuotation(selectedQuotation?.id)}>
              <Check className="h-4 w-4 mr-2" /> Approve & Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              Reject Quotation
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this quotation.
            </DialogDescription>
          </DialogHeader>
          
          {selectedQuotation && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Quotation:</span>
                  <span className="font-semibold">{selectedQuotation.quote_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client:</span>
                  <span className="font-semibold">{selectedQuotation.client_name}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Rejection Reason (Optional)</Label>
                <Input
                  placeholder="Enter reason for rejection..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setRejectDialogOpen(false);
              setRejectionReason('');
            }}>Cancel</Button>
            <Button variant="destructive" onClick={() => rejectQuotation(selectedQuotation?.id)}>
              <XCircle className="h-4 w-4 mr-2" /> Reject Quotation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Viewer for Print/Email */}
      <DocumentViewer
        open={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        document={selectedQuotation}
        type="quotation"
        template={template}
        onEmailSent={() => fetchQuotations()}
      />
    </div>
  );
};

export default Quotations;
